import { useEffect, useState } from 'react'
import type { Attachment } from '@/components/AttachmentPicker'
import { t } from '@/lib/i18n'

/**
 * 稿子的 `#attViewer`：全屏看一张照片。
 *
 * 这是**居中全屏**而不是底部 sheet，和转盘 / 庆祝卡同一个理由（差距审计 §5②）：
 * sheet 是给「要伸手去填东西」的表单和菜单用的；照片是**看**的东西。
 *
 * z-[80]：压在 sheet（71）之上 —— 从打卡证据那个 sheet 里点开一张照片时
 * 它必须在上面。与 Spin / CelebrationCard 同层。
 *
 * 对元素类型泛化，理由同 AudioList：相册页要在 onDelete 里读调用方自己的
 * `source` 字段来选删除路由，不泛化就得在调用点加一个 `as`。
 */
export default function PhotoViewer<T extends Attachment>({ photo, onClose, onDelete }: {
  photo: T | null
  onClose: () => void
  onDelete?: (photo: T) => void
}) {
  // 删除**过一层确认**：仓库的约定是「删除不给撤销，所以它前面加二次确认」
  // （见 Toast.tsx 的文档注释、条目详情 sheet、目标删除）。照片尤其如此 ——
  // purge 之后文件真的没了，做不出撤销来。
  const [armed, setArmed] = useState(false)

  useEffect(() => {
    if (!photo) return

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [ photo, onClose ])

  // 换一张照片就把「已举起」的状态放下 —— 不然上一张按了一次删，
  // 下一张一点就直接删掉了。
  const [armedFor, setArmedFor] = useState<number | null>(null)
  if (photo && armedFor !== photo.id) {
    setArmedFor(photo.id)
    setArmed(false)
  }

  if (!photo) return null

  return (
    <div
      role="dialog"
      aria-label={t('homework.photo_viewer_label')}
      className="fixed inset-0 z-[80] bg-black/80 flex flex-col"
    >
      <div className="flex items-center justify-between px-3 pt-safe">
        <button
          type="button"
          onClick={onClose}
          aria-label={t('layout.close')}
          className="min-w-tap min-h-tap text-white text-xl"
        >
          ✕
        </button>
        {onDelete && (
          armed ? (
            <span className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => onDelete(photo)}
                className="min-h-tap px-3 rounded-chip bg-red text-white text-[13px] font-bold"
              >
                {t('homework.photo_delete_confirm')}
              </button>
              <button
                type="button"
                onClick={() => setArmed(false)}
                className="min-h-tap px-3 rounded-chip text-white text-[13px] font-semibold"
              >
                {t('people.keep')}
              </button>
            </span>
          ) : (
            <button
              type="button"
              onClick={() => setArmed(true)}
              className="min-h-tap px-3 rounded-chip text-white text-[13px] font-bold active:bg-red"
            >
              🗑️ {t('homework.photo_delete')}
            </button>
          )
        )}
      </div>
      {/* 点图片本身也关掉：全屏看图时最自然的退出手势就是点一下 */}
      <button type="button" onClick={onClose} className="flex-1 min-h-0 p-3 flex items-center justify-center">
        <img src={photo.url} alt={t('homework.photo_alt')} className="max-w-full max-h-full object-contain" />
      </button>
    </div>
  )
}
